import type { ComponentType } from "react";
import * as React from "react";
import * as ReactJsxRuntime from "react/jsx-runtime";
import * as Remotion from "remotion";
import * as RemotionPaths from "@remotion/paths";
import type { CustomComponentSchema } from "./custom-component-schema";
import {
  CUSTOM_COMPONENT_PREFIX,
  isCustomComponentLayer,
  type VideoComposition,
} from "./types";

/**
 * `<Img>` with `pauseWhenLoading` on by default, so an image inside an
 * agent-written component holds the Player the way every built-in layer's
 * images do. Component authors are shown bare `<Img src=…>` in the generation
 * prompt and have no reason to opt in themselves; an explicit `pauseWhenLoading`
 * in the component still wins.
 */
const BufferingImg: typeof Remotion.Img = (props) =>
  React.createElement(Remotion.Img, { pauseWhenLoading: true, ...props });

/**
 * What `import … from "remotion"` resolves to inside a custom component: the
 * real module, with the tags we hold to a stricter default replaced.
 */
const CUSTOM_COMPONENT_REMOTION = { ...Remotion, Img: BufferingImg };

/**
 * Packages an agent-generated custom component is allowed to `require()`.
 * `transpile-custom-components.ts` (server-side) marks these as externals
 * during esbuild, so any other `import` would have been bundled — if it
 * still shows up here it's a bypass attempt and we want to fail loudly.
 */
const ALLOWED_DEPS: Record<string, unknown> = {
  react: React,
  // The automatic JSX runtime emits `require("react/jsx-runtime")` for any
  // JSX, so the loader must provide it even though authors never import it
  // directly.
  "react/jsx-runtime": ReactJsxRuntime,
  remotion: CUSTOM_COMPONENT_REMOTION,
  "@remotion/paths": RemotionPaths,
};

function customRequire(pkg: string): unknown {
  const dep = ALLOWED_DEPS[pkg];
  if (!dep) {
    throw new Error(
      `Custom component tried to import unavailable package: "${pkg}". ` +
        `Only "react", "remotion", and "@remotion/paths" are available.`,
    );
  }
  return dep;
}

/**
 * Cache by presigned URL. Presigned URLs change between sessions but are
 * stable within a session (both the editor preview and the Lambda/local
 * render resolve them once up front), so a URL hit reliably means "same
 * code" within the render context's lifetime.
 */
const componentCache = new Map<string, ComponentType<unknown>>();
const schemaCache = new Map<string, CustomComponentSchema>();

/**
 * Fetch a transpiled `.js` custom component from a presigned URL and
 * evaluate it as a CommonJS module. Returns the default export (or the
 * module itself if no `default` field).
 *
 * No credentials/two-step fetch — the caller has already resolved the URL
 * (the editor via `preResolveAssets`, the export pipeline via
 * `buildAssetMap`).
 */
export async function loadCustomComponent(
  presignedUrl: string,
): Promise<ComponentType<unknown>> {
  const cached = componentCache.get(presignedUrl);
  if (cached) return cached;

  const res = await fetch(presignedUrl);
  if (!res.ok) {
    throw new Error(
      `Failed to fetch custom component JS: ${res.status} ${res.statusText}`,
    );
  }
  const jsCode = await res.text();

  // esbuild transpiles with `format: "cjs"`, so the file uses
  // `require()` + `module.exports`. Build the shim and run it.
  const exports: Record<string, unknown> = {};
  const module = { exports };
  const fn = new Function("require", "module", "exports", jsCode);
  fn(customRequire, module, exports);

  const mod = module.exports as Record<string, unknown>;
  const Component = (mod.default ?? mod) as ComponentType<unknown>;

  componentCache.set(presignedUrl, Component);
  return Component;
}

async function loadCustomComponentSchema(
  presignedUrl: string,
): Promise<CustomComponentSchema> {
  const cached = schemaCache.get(presignedUrl);
  if (cached) return cached;
  const res = await fetch(presignedUrl);
  if (!res.ok) {
    throw new Error(
      `Failed to fetch custom component schema: ${res.status} ${res.statusText}`,
    );
  }
  const schema = (await res.json()) as CustomComponentSchema;
  schemaCache.set(presignedUrl, schema);
  return schema;
}

export interface CustomComponentBundle {
  /** React components keyed by transpiled `.js` filename. */
  components: Record<string, ComponentType<unknown>>;
  /**
   * Schemas keyed by the same `.js` filename as `components`. Missing
   * entries mean the component shipped without a sidecar or its sidecar
   * failed to load — the renderer falls back to passing the
   * composition's props through unchanged.
   */
  schemas: Record<string, CustomComponentSchema>;
}

/**
 * Walk a composition for `./assets/*.tsx` (or `.js`) layer references,
 * resolve each one to its presigned URL via `componentAssets`, and load
 * the components and their schema sidecars in parallel.
 *
 * A missing or invalid schema is logged but does not fail the load —
 * the component still mounts; it just won't have schema defaults filled
 * in for unset props. A missing component is skipped for that ref.
 */
export async function preloadCustomComponents(
  composition: VideoComposition,
  componentAssets: Record<string, string>,
): Promise<CustomComponentBundle> {
  const customRefs = composition.layers
    .filter(isCustomComponentLayer)
    .map((l) => l.component);

  if (customRefs.length === 0) return { components: {}, schemas: {} };

  const uniqueRefs = [...new Set(customRefs)];
  const components: Record<string, ComponentType<unknown>> = {};
  const schemas: Record<string, CustomComponentSchema> = {};

  await Promise.all(
    uniqueRefs.map(async (ref) => {
      // `./assets/slideshow.tsx` → `slideshow.js`, `slideshow.schema.json`
      const jsFilename = ref
        .slice(CUSTOM_COMPONENT_PREFIX.length)
        .replace(/\.tsx$/, ".js");
      const schemaFilename = jsFilename.replace(/\.js$/, ".schema.json");

      const jsUrl = componentAssets[jsFilename];
      if (!jsUrl) {
        console.warn(
          `[ad-studio-renderer] custom component "${ref}": no asset URL for "${jsFilename}"`,
        );
        return;
      }

      const schemaUrl = componentAssets[schemaFilename];
      // Run component and schema fetches in parallel — they're
      // independent, and the schema is best-effort.
      const [component] = await Promise.all([
        loadCustomComponent(jsUrl),
        schemaUrl
          ? loadCustomComponentSchema(schemaUrl)
              .then((schema) => {
                schemas[jsFilename] = schema;
              })
              .catch((err: Error) => {
                console.warn(
                  `[ad-studio-renderer] custom component "${ref}": failed to load schema (${schemaFilename}): ${err.message}`,
                );
              })
          : Promise.resolve().then(() => {
              console.warn(
                `[ad-studio-renderer] custom component "${ref}": no schema sidecar at "${schemaFilename}"`,
              );
            }),
      ]);

      components[jsFilename] = component;
    }),
  );

  return { components, schemas };
}
